import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, Dialog, Typography, SxProps, Theme } from '@mui/material';

const STRINGS = appStrings.modules.majorsGuide.majorsExplorerInstructionsModal;

/**
 * Configuration for a single instruction slide
 */
type InstructionSlide = Readonly<{
  imageUrl: string;
  title: string;
  description: string;
}>;

/**
 * Props for MajorsExplorerInstructionsModal
 */
export type MajorsExplorerInstructionsModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
}>;

/**
 * Carousel content - 3 instruction slides
 */
const slides: InstructionSlide[] = [
  {
    imageUrl: '/images/explore_majors_instructions_1.png',
    title: STRINGS.slides[0].title,
    description: STRINGS.slides[0].description,
  },
  {
    imageUrl: '/images/explore_majors_instructions_2.png',
    title: STRINGS.slides[1].title,
    description: STRINGS.slides[1].description,
  },
  {
    imageUrl: '/images/explore_majors_instructions_3.png',
    title: STRINGS.slides[2].title,
    description: STRINGS.slides[2].description,
  },
];

/**
 * MajorsExplorerInstructionsModal - Carousel-style modal that introduces users
 * to the Majors Explorer (Career Relevance) tool with step-by-step guidance.
 *
 * Features:
 * - 3 slides with images, titles, and descriptions
 * - Pagination dots showing current slide
 * - Skip button (closes modal)
 * - Next button (advances to next slide, closes on last slide)
 */
export function MajorsExplorerInstructionsModal({
  open,
  onClose,
}: MajorsExplorerInstructionsModalProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNext = () => {
    if (currentSlide === slides.length - 1) {
      // On last slide, close modal
      onClose();
    } else {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleExited = () => {
    // Reset slide after modal has fully closed to avoid flash
    setCurrentSlide(0);
  };

  const currentContent = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
      TransitionProps={{ onExited: handleExited }}
    >
      <Box sx={containerSx}>
        {/* Image Section */}
        <Box sx={imageContainerSx}>
          <Box
            component="img"
            src={currentContent.imageUrl}
            alt={currentContent.title}
            sx={imageSx}
          />
        </Box>

        {/* Content Section */}
        <Box sx={contentSx}>
          <Typography sx={titleSx}>{currentContent.title}</Typography>
          <Typography sx={descriptionSx}>{currentContent.description}</Typography>
        </Box>

        {/* Pagination Dots */}
        <Box sx={paginationContainerSx}>
          {slides.map((_, index) => (
            <Box key={index} sx={paginationDotSx(index === currentSlide)} />
          ))}
        </Box>

        {/* Button Actions */}
        <Box sx={buttonsContainerSx}>
          <Button
            id="majors-explorer-instructions-skip-btn"
            variant="contained"
            size="md"
            hierarchy="Secondary gray"
            onClick={handleSkip}
            sx={buttonSx}
          >
            {STRINGS.skipButton}
          </Button>
          <Button
            id="majors-explorer-instructions-next-btn"
            variant="contained"
            size="md"
            hierarchy="Primary"
            onClick={handleNext}
            sx={buttonSx}
          >
            {isLastSlide ? STRINGS.doneButton : STRINGS.nextButton}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

// Styles

const dialogPaperSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-xl'],
  boxShadow: `8px 8px 0px 0px ${backrs.colors['Colors/Base/Black']}`,
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  overflow: 'hidden',
});

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const imageContainerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-3xl'],
});

const imageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '320px', // Hardcoded: consistent height across slides
  objectFit: 'cover',
  display: 'block',
  border: `1px solid ${backrs.colors['Gray/500']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  px: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-2xl'],
  textAlign: 'center',
});

const titleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-lg-semibold'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const paginationContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xl'],
  px: backrs.spacing['spacing-3xl'],
});

const paginationDotSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '10px', // Hardcoded: pagination dot size
    height: '10px', // Hardcoded: pagination dot size
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: isActive
      ? backrs.colors['Colors/Foreground/fg-brand-primary-alt']
      : backrs.colors['Colors/Background/bg-quaternary'],
    transition: 'background-color 0.2s ease-in-out',
  });

const buttonsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  pt: backrs.spacing['spacing-4xl'],
  pb: backrs.spacing['spacing-3xl'],
  px: backrs.spacing['spacing-3xl'],
});

const buttonSx: SxProps<Theme> = {
  flex: 1,
};
